import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateInterventionList,
  updateIndexOnList,
  resetError,
} from "./SwitchCycleList.actions";
import Col from "react-bootstrap/Col";
import {
  DEFAULT_SWIM_SORT,
  SWIM_CYCLE_FILTER,
  DEFAULT_FILTER,
} from "lookup/SwimConstants";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import { MdHelp } from "react-icons/md";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import SwitchListCommon from "../SwitchList/SwitchListCommon";
import { COLUMN } from "../SwitchList/CreateColumnListForSwitchList";
import { isLoggedInUserBioSimilarUser } from "services/AWUtilityService";
import { SWITCH_NUMBER_OP } from "lookup/GlobalConstants";
import { SWITCH_NUMBER_STRING } from "lookup/GlobalConstants";
import { updateSelectedSwitches } from "./SwitchCycleList.actions";

const COLUMN_LIST = [
  COLUMN.customerNameAbbreviation,
  COLUMN.clientNameAbbreviation,
  COLUMN.switchNumber,
  COLUMN.generatedDate,
  COLUMN.projectedSavings,
  COLUMN.cycleState,
  COLUMN.cycleStatus,
  COLUMN.hasOpenTaskActivity,
  COLUMN.hasPrescriberInReview,
  COLUMN.activityBlocked,
];

const DEFAULT_PAGE_SIZE = 15;

const SwitchCycleList = ({ // Removed Redux props
  selectedClientNameAbbreviation,
  populationType,
  setSelectedSwitches,
  setBioSimilarSwitchesToggle,
  bioSimilarSwitchesToggle,
  moveToTheListView,
}) => {
  const dispatch = useDispatch();
  const {
    data,
    pages,
    totalElements,
    loading,
    listState,
    isError,
    wfItemIds: propWfItemIds,
    selected,
  } = useSelector((state) => {
    let listState = state.switchCycleList.listState;
    if (!listState) {
      listState = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: DEFAULT_SWIM_SORT,
        filtered: [
          ...SWIM_CYCLE_FILTER.default,
          { id: "priorityAgent", value: false },
        ],
        resized: [],
        expanded: {},
      };
      listState.filtered = [
        ...listState.filtered,
        {
          id: "clientNameAbbreviation",
          value: selectedClientNameAbbreviation,
          isdefault: true,
        },
      ];
    }
    return {
      data: state.switchCycleList.data || [],
      pages: state.switchCycleList.pages || 0,
      totalElements: state.switchCycleList.totalElements ?? "Loading ...",
      loading: state.switchCycleList.loading,
      listState: listState,
      isError: state.switchCycleList.isError,
      wfItemIds: state.switchCycleList.wfItemIds || [],
      selected: state.switchCycleList.selectedSwitches || [],
    };
  });

  const tableRef = useRef(null);

  const [isSelectAll, setIsSelectAll] = useState(false);
  const [toggleAllSwitches, setToggleAllSwitches] = useState(false);
  const [toggleDefaultFilters, setToggleDefaultFilters] = useState(true);
  const [wfItemIds, setWfItemIds] = useState(propWfItemIds);
  const [resetTableSelection, setResetTableSelection] = useState(false);

  const updateData = useCallback((state, preventUpdate) => {
    if (!preventUpdate) {
      const filteredForState = state.filtered.filter(
        (element) =>
          !(element.id === "state" && element.value.includes(DEFAULT_FILTER))
      );
      if (filteredForState.length !== state.filtered.length) {
        filteredForState.push(SWIM_CYCLE_FILTER.defaultStateFrontEnd);
        state.filtered = filteredForState;
      }

      const index_status = state.filtered.findIndex(
        (element) => element.id === "status"
      );

      if (index_status !== -1) {
        state.filtered[index_status].value = state.filtered[
          index_status
        ].value.map((value) =>
          typeof value === "string" ? value : value.name
        );
      }

      const switchNumberIndex = state.filtered.findIndex(
        (element) => element.id === "switchNumber"
      );
      if (switchNumberIndex !== -1) {
        state.filtered[switchNumberIndex].operator = SWITCH_NUMBER_OP;
        state.filtered[switchNumberIndex].id = SWITCH_NUMBER_STRING;
      }

      const filteredForStatus = state.filtered.filter(
        (element) =>
          !(element.id === "status" && element.value.includes(DEFAULT_FILTER))
      );
      if (filteredForStatus.length !== state.filtered.length) {
        filteredForStatus.push(SWIM_CYCLE_FILTER.defaultStatusFrontEnd);
        state.filtered = filteredForStatus;
      }
      const clientFilterIndex = state.filtered.findIndex(
        (element) => element.id === "clientNameAbbreviation"
      );
      if (clientFilterIndex !== -1) {
        state.filtered.splice(clientFilterIndex, 1);
      }
      const populationTypeIndex = state.filtered.findIndex(
        (element) => element.id === "populationType"
      );
      if (populationTypeIndex !== -1) {
        state.filtered.splice(populationTypeIndex, 1);
      }
      const filteredForTaskIndex = state.filtered.findIndex(
        (element) =>
          element.id === "hasOpenTaskActivity" && element.value.includes("All")
      );
      if (filteredForTaskIndex !== -1) {
        state.filtered.splice(filteredForTaskIndex, 1);
      }
      const filteredForHasPrescriberInReviewIndex = state.filtered.findIndex(
        (element) =>
          element.id === "hasPrescriberInReview" &&
          element.value.includes("All")
      );
      if (filteredForHasPrescriberInReviewIndex !== -1) {
        state.filtered.splice(filteredForHasPrescriberInReviewIndex, 1);
      }

      state.filtered.push({
        id: "clientNameAbbreviation",
        value: selectedClientNameAbbreviation,
        isdefault: true,
      });

      if (populationType) {
        state.filtered.push({
          id: "populationType",
          value: [populationType],
        });
      }

      dispatch(updateInterventionList(
        state.pageSize,
        state.page,
        state.sorted,
        state.filtered,
        state.resized,
        state.expanded
      ));
    }
  }, [selectedClientNameAbbreviation, populationType, dispatch]);

  useEffect(() => {
    updateData(listState);
  }, []);

  useEffect(() => {
    if (moveToTheListView) {
      tableRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [moveToTheListView]);

  const toggleSelection = useCallback((selectedRows) => {
    const selectedWfItemIds = Object.keys(selectedRows).map((row) => row);
    setSelectedSwitches(selectedWfItemIds);
  }, [setSelectedSwitches]);

  const selectAll = useCallback(() => {
    let isAllSelected = true;
    data.forEach((x) => {
      if (!selected.includes(x.wfItemId)) {
        isAllSelected = false;
        return;
      }
    });
    return isAllSelected;
  }, [data, selected]);

  const toggleAll = useCallback(() => {
    let currentSelected = [...selected];
    let isAllCurrentlySelected = selectAll();

    if (isAllCurrentlySelected) {
      // Deselect all
      currentSelected = [];
    } else {
      // Select all
      data.forEach((item) => {
        if (!currentSelected.includes(item.wfItemId)) {
          currentSelected.push(item.wfItemId);
        }
      });
    }
    setSelectedSwitches(currentSelected);
  }, [data, selected, selectAll, setSelectedSwitches]);

  const selectAllRecords = useCallback((newWfItemIds, checked) => {
    dispatch(updateSelectedSwitches(newWfItemIds));
    setSelectedSwitches(newWfItemIds);
    setIsSelectAll(checked);
  }, [dispatch, setSelectedSwitches]);

  const getSwitchesForSelectAll = useCallback((e) => {
    const { checked } = e.target;
    const { filtered, sorted } = listState;
    updateData(listState); // Ensure data is up-to-date before fetching
    if (checked) {
      sendAuthenticatedAsyncRequest(
        `/swim/get-switches-for-select-all`,
        "POST",
        { filtered, sorted },
        (r) => {
          setWfItemIds(r.data);
          setToggleAllSwitches(checked);
          selectAllRecords(r.data, checked);
        }
      );
    } else {
      setWfItemIds([]);
      setToggleAllSwitches(checked);
      selectAllRecords([], checked);
    }
  }, [listState, updateData, selectAllRecords]);

  const getSwitchesByToggle = useCallback((e) => {
    const { checked } = e.target;
    setToggleDefaultFilters(checked);
    const { filtered } = listState;
    let updatedFiltered = [...filtered];
    if (checked) {
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "state" && element.id !== "status"
      );
      updatedFiltered.push(...SWIM_CYCLE_FILTER.default);
    } else {
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "state" && element.id !== "status"
      );
    }
    updateData({
      ...listState,
      filtered: updatedFiltered,
    });
  }, [listState, updateData]);

  const fetchBioSimilarSwitches = useCallback((e) => {
    const { checked } = e.target;
    const { filtered, sorted } = listState;
    let updatedFiltered = [...filtered];
    if (checked) {
      setBioSimilarSwitchesToggle(true);
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "priorityAgent"
      );
      updatedFiltered.push({
        id: "priorityAgent",
        value: true,
      });
    } else {
      setBioSimilarSwitchesToggle(false);
      updatedFiltered = updatedFiltered.filter(
        (element) => element.id !== "priorityAgent"
      );
      updatedFiltered.push({
        id: "priorityAgent",
        value: false,
      });
    }
    updateData({
      ...listState,
      filtered: updatedFiltered,
      sorted,
    });
  }, [listState, updateData, setBioSimilarSwitchesToggle]);

  return (
    <Col md={{ span: 15 }} style={{ paddingTop: "10px" }}>
      <div ref={tableRef} />
      <div style={{ paddingBottom: "10px" }}>
        <Card variant="primary">
          <Card.Body>
            <Row>
              <Col>
                {selectedClientNameAbbreviation.length > 0 &&
                  selectedClientNameAbbreviation[0] === "WMT" &&
                  isLoggedInUserBioSimilarUser() && (
                    <Form.Check
                      type="switch"
                      id="bioSimilarSwitchesToggle"
                      label="Bio Similar Switches"
                      onChange={fetchBioSimilarSwitches}
                      checked={bioSimilarSwitchesToggle}
                    />
                  )}
              </Col>
              </Row>
              <Row>
              <Col>
                <Form.Check
                  type="switch"
                  id="selectAll"
                  label="Select All Switches"
                  onChange={getSwitchesForSelectAll}
                  style={{ display: "inline", paddingRight: "5px" }}
                  checked={toggleAllSwitches}
                ></Form.Check>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip>
                      This will select all the switches from all pages
                    </Tooltip>
                  }
                >
                  {({ ref, ...triggerHandler }) => (
                    <span ref={ref} {...triggerHandler}>
                      <MdHelp
                        style={{ color: "#FFAA3B", cursor: "pointer" }}
                      ></MdHelp>
                    </span>
                  )}
                </OverlayTrigger>
                <Form.Check
                  type="switch"
                  id="defaultFilters"
                  label="Default Filters"
                  onChange={getSwitchesByToggle}
                  style={{
                    display: "inline",
                    paddingRight: "5px",
                    marginLeft: "30px",
                  }}
                  checked={toggleDefaultFilters}
                ></Form.Check>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip>
                      This will remove all the default filters
                    </Tooltip>
                  }
                >
                  {({ ref, ...triggerHandler }) => (
                    <span ref={ref} {...triggerHandler}>
                      <MdHelp
                        style={{ color: "#FFAA3B", cursor: "pointer" }}
                      ></MdHelp>
                    </span>
                  )}
                </OverlayTrigger>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
      <SwitchListCommon
        updateInterventionList={(...args) => dispatch(updateInterventionList(...args))}
        updateIndexOnList={(...args) => dispatch(updateIndexOnList(...args))}
        resetError={() => dispatch(resetError())}
        data={data}
        pages={pages}
        totalElements={totalElements}
        loading={loading}
        listState={listState}
        isError={isError}
        updateData={updateData}
        columnList={COLUMN_LIST}
        selectedClient={selectedClientNameAbbreviation}
        isSortFilterEnabledForCust={false}
        toggleSelection={toggleSelection}
        isSelectAll={isSelectAll}
        selected={selected}
        resetTableSelection={resetTableSelection}
        isViewable={false}
      />
    </Col>
  );
};

export default SwitchCycleList;
